import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { developerApi } from '../../api/developer.api';
import { useAuthStore } from '../../store/auth.store';
import type {
  DeveloperProfileResponseDto,
  UpdateDeveloperProfileDto,
  ChangePasswordDto,
} from '../../types';

export function useDeveloperProfile() {
  return useQuery<DeveloperProfileResponseDto>({
    queryKey: ['developer', 'profile'],
    queryFn: async () => {
      const { data } = await developerApi.get('/v1/developer/profile');
      return data.data || data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateDeveloperProfile() {
  const queryClient = useQueryClient();
  return useMutation<DeveloperProfileResponseDto, Error, UpdateDeveloperProfileDto>({
    mutationFn: async (payload) => {
      const { data } = await developerApi.patch('/v1/developer/profile', payload);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'profile'] });
    },
  });
}

export function useUploadDeveloperAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);

      const { data } = await developerApi.post('/v1/developer/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'profile'] });
    },
  });
}

/**
 * Toggle developer 2FA. PATCH /v1/developer/profile ignores `twoFactorEnabled`,
 * so this dedicated endpoint (requires the current password) must be used.
 */
export function useSetDeveloperTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { enabled: boolean; currentPassword: string }>({
    mutationFn: async (payload) => {
      const { data } = await developerApi.patch('/v1/developer/profile/two-factor', payload);
      return data.data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<DeveloperProfileResponseDto>(['developer', 'profile'], (prev) =>
        prev ? { ...prev, twoFactorEnabled: variables.enabled } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ['developer', 'profile'] });
    },
  });
}

export function useChangeDeveloperPassword() {
  return useMutation<
    unknown,
    AxiosError<{ message?: string | string[] }>,
    ChangePasswordDto
  >({
    mutationFn: async (payload: ChangePasswordDto) => {
      const { data } = await developerApi.patch('/v1/developer/password', payload);
      return data.data || data;
    },
  });
}

export function useDeveloperLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await developerApi.post('/v1/auth/logout');
      return data;
    },
    onSettled: () => {
      useAuthStore.getState().logout();
      queryClient.clear();
    },
  });
}

export function useDeveloperRefreshToken() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await developerApi.post('/api/auth/session/refresh');
      return data.data || data;
    },
  });
}

export { useUploadDeveloperAvatar as useUploadAvatar };

